import type { SongSearchResult } from '@/types'

interface DeezerTrack {
  id: number
  title: string
  artist: { name: string }
  duration: number
  album?: { title: string }
  release_date?: string
}

function parseYear(dateStr?: string): number | null {
  if (!dateStr) return null
  const y = parseInt(dateStr.substring(0, 4), 10)
  return isNaN(y) ? null : y
}

export async function searchDeezer(query: string): Promise<SongSearchResult[]> {
  const url = `/api/deezer/search?q=${encodeURIComponent(query)}&limit=20`
  const res = await fetch(url)
  if (!res.ok) return []
  const json = await res.json()
  // Deezer reports errors (e.g. "Quota limit exceeded") as HTTP 200 with an error body
  if (json.error) throw new Error(`Deezer: ${json.error.message ?? 'error'}`)
  const tracks: DeezerTrack[] = json.data ?? []

  return tracks.map((t) => ({
    title: t.title,
    artist: t.artist.name,
    duration_seconds: t.duration || null,
    year: parseYear(t.release_date),
    source: 'deezer' as const,
    source_id: String(t.id),
  }))
}

export async function fetchDeezerTrackYear(trackId: string): Promise<number | null> {
  try {
    const res = await fetch(`/api/deezer/track/${trackId}`)
    if (!res.ok) return null
    const json = await res.json()
    return parseYear(json.release_date)
  } catch {
    return null
  }
}

interface ITunesTrack {
  trackId: number
  trackName: string
  artistName: string
  trackTimeMillis?: number
  releaseDate?: string
}

// Called from the browser (iTunes sends CORS headers), so it uses the user's IP, not Cloud Run's shared one.
export async function searchItunes(query: string): Promise<SongSearchResult[]> {
  const url = `https://itunes.apple.com/search?term=${encodeURIComponent(query)}&media=music&entity=song&limit=20`
  const res = await fetch(url)
  if (!res.ok) return []
  const json = await res.json()
  const tracks: ITunesTrack[] = json.results ?? []

  return tracks.map((t) => ({
    title: t.trackName,
    artist: t.artistName,
    duration_seconds: t.trackTimeMillis ? Math.round(t.trackTimeMillis / 1000) : null,
    year: parseYear(t.releaseDate),
    source: 'itunes' as const,
    source_id: String(t.trackId),
  }))
}

export async function searchSongs(query: string): Promise<SongSearchResult[]> {
  try {
    const results = await searchDeezer(query)
    if (results.length > 0) return results
  } catch {
    // fall through to iTunes
  }
  return searchItunes(query)
}
